import { useState, useEffect } from 'react';
import { clientsAPI, projectsAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, User, Mail, Phone, MapPin, MoreVertical, Edit, Trash2, Loader2, FolderKanban, ArrowRight } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';

export default function Clients() {
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [projectsDialogOpen, setProjectsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientProjects, setClientProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  const fetchClients = async () => {
    try {
      const res = await clientsAPI.getAll();
      setClients(res.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', address: '', notes: '' });
    setEditingClient(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (client) => {
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      notes: client.notes || ''
    });
    setEditingClient(client);
    setDialogOpen(true);
  };

  const openProjectsDialog = async (client) => {
    setSelectedClient(client);
    try {
      const projectsRes = await clientsAPI.getProjects(client.id);
      setClientProjects(projectsRes.data);
      setProjectsDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load client projects');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Client name is required');
      return;
    }

    setSaving(true);
    try {
      const data = { ...formData };
      if (editingClient) {
        await clientsAPI.update(editingClient.id, data);
        toast.success(t('clientUpdated'));
      } else {
        await clientsAPI.create(data);
        toast.success(t('clientAdded'));
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`${t('delete')} ${client.name}?`)) return;
    
    try {
      await clientsAPI.delete(client.id);
      toast.success(t('clientDeleted'));
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-[#78866B]/10 text-[#78866B]',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-[#A8A29E]/10 text-[#A8A29E]',
      on_hold: 'bg-yellow-100 text-yellow-700'
    };
    const labels = {
      active: t('active'),
      in_progress: t('inProgress'),
      completed: t('completed'),
      on_hold: t('onHold')
    };
    return (
      <Badge className={`${styles[status] || styles.active} border-0 rounded-full text-xs`}>
        {labels[status] || status}
      </Badge>
    );
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 w-48 bg-[#E7E5E4] rounded-lg animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-48 bg-[#E7E5E4] rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-medium text-[#292524] tracking-tight">{t('clients')}</h1>
          <p className="text-[#78716C] mt-1">{clients.length} {t('totalClientsCount')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90 h-11 px-6"
              onClick={openAddDialog}
              data-testid="add-client-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('addClient')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium">
                {editingClient ? t('editClient') : t('addNewClient')}
              </DialogTitle>
              <DialogDescription className="sr-only">Client form</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('clientName')} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('clientName')}
                  className="h-11"
                  data-testid="client-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                    className="h-11"
                    data-testid="client-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phone')}</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="h-11"
                    data-testid="client-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                  <Label htmlFor="address">{t('address')}</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St"
                    className="h-11"
                    data-testid="client-address-input"
                  />
                </div>
              <div className="space-y-2">
                <Label htmlFor="notes">{t('notes')}</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder={t('notes')}
                  rows={3}
                  data-testid="client-notes-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
                  {t('cancel')}
                </Button>
                <Button 
                  type="submit" 
                  className="rounded-full bg-[#57534E] hover:bg-[#57534E]/90"
                  disabled={saving}
                  data-testid="save-client-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingClient ? t('update') : t('add')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716C]" />
        <Input
          placeholder={t('searchClients')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-11 rounded-xl"
          data-testid="search-clients-input"
        />
      </div>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <User className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
            <p className="text-[#78716C]">
              {searchQuery ? t('noClientsFound') : t('noClientsYet')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-6">
          {filteredClients.map((client, index) => (
            <Card 
              key={client.id}
              className={`border-0 shadow-sm hover:shadow-md transition-all duration-200 animate-slide-in stagger-${Math.min(index + 1, 5)} cursor-pointer`}
              onClick={() => openProjectsDialog(client)}
              data-testid={`client-card-${client.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#78866B]/10 flex items-center justify-center">
                      <span className="text-lg font-medium text-[#78866B]">
                        {client.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-medium text-[#292524]">{client.name}</h3>
                      {client.email && (
                        <p className="text-sm text-[#78716C]">{client.email}</p>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`client-menu-${client.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openProjectsDialog(client); }} data-testid={`view-projects-${client.id}`}>
                        <FolderKanban className="w-4 h-4 mr-2" />
                        {t('viewProjects')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(client); }} data-testid={`edit-client-${client.id}`}>
                        <Edit className="w-4 h-4 mr-2" />
                        {t('edit')}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); handleDelete(client); }}
                        className="text-red-600"
                        data-testid={`delete-client-${client.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t('delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                <div className="space-y-2 text-sm">
                  {client.phone && (
                    <div className="flex items-center gap-2 text-[#78716C]">
                      <Phone className="w-4 h-4" />
                      <span>{client.phone}</span>
                    </div>
                  )}
                  {client.address && (
                    <div className="flex items-center gap-2 text-[#78716C]">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate">{client.address}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Client Details Dialog */}
      <Dialog open={projectsDialogOpen} onOpenChange={setProjectsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">
              {selectedClient?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">Client details</DialogDescription>
          </DialogHeader>
          
          {/* Client Information */}
          {selectedClient && (
            <div className="space-y-3 mb-6 p-4 rounded-xl bg-[#F5F5F4]/50">
              {selectedClient.email && (
                <div className="flex items-center gap-2 text-sm text-[#78716C]">
                  <Mail className="w-4 h-4" />
                  <span>{selectedClient.email}</span>
                </div>
              )}
              {selectedClient.phone && (
                <div className="flex items-center gap-2 text-sm text-[#78716C]">
                  <Phone className="w-4 h-4" />
                  <span>{selectedClient.phone}</span>
                </div>
              )}
              {selectedClient.address && (
                <div className="flex items-center gap-2 text-sm text-[#78716C]">
                  <MapPin className="w-4 h-4" />
                  <span>{selectedClient.address}</span>
                </div>
              )}
              {selectedClient.notes && (
                <div className="text-sm text-[#78716C] pt-2 border-t border-[#E7E5E4]">
                  <p>{selectedClient.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Projects List */}
          <h4 className="text-sm font-medium text-[#78716C] uppercase tracking-wide mb-3">{t('projects')}</h4>
          {clientProjects.length === 0 ? (
            <div className="py-8 text-center">
              <FolderKanban className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
              <p className="text-[#78716C]">{t('noProjectsForClient')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {clientProjects.map((project) => (
                <Card 
                  key={project.id} 
                  className="border-0 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
                  onClick={() => { setProjectsDialogOpen(false); window.location.href = '/projects'; }}
                  data-testid={`client-project-${project.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-[#292524]">{project.name}</h4>
                          {getStatusBadge(project.status)}
                        </div>
                        {project.description && (
                          <p className="text-sm text-[#78716C] line-clamp-1">{project.description}</p>
                        )}
                        {project.start_date && (
                          <p className="text-xs text-[#78716C] mt-1">
                            {format(new Date(project.start_date), 'MMM d, yyyy')}
                            {project.end_date && ` - ${format(new Date(project.end_date), 'MMM d, yyyy')}`}
                          </p>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#A8A29E]" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setProjectsDialogOpen(false)} className="rounded-full">
              {t('close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
